import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Bell, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { Button } from '@t-one-internal/design-system';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import apiClient from '../../api/apiClient';

interface Notification {
  id: number;
  title: string;
  content?: string;
  category: string;
  priority: string;
  is_read: boolean;
  sent_by_name?: string;
  created_at: string;
}

interface NotificationsResponse {
  message: string;
  data: Notification[];
  count: number;
}

interface UnreadCountResponse {
  message: string;
  data: {
    count: number;
  };
}

const PAGE_SIZE = 10;

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const hasMore = notifications.length < totalCount;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Every minute
    return () => clearInterval(interval);
  }, []);

  // Listen for notification-read events from other micro-frontends
  useEffect(() => {
    const handleNotificationRead = () => {
      fetchUnreadCount();
    };
    window.addEventListener('notification-read', handleNotificationRead);
    return () => window.removeEventListener('notification-read', handleNotificationRead);
  }, []);

  // Fetch notifications when dropdown opens (reset to page 1)
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const fetchUnreadCount = async () => {
    try {
      const response = await apiClient.get<UnreadCountResponse>('/user-notifications/count');
      setUnreadCount(response.data.data?.count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    setPage(1);
    try {
      const response = await apiClient.get<NotificationsResponse>('/user-notifications/', {
        params: { limit: PAGE_SIZE, offset: 0 }
      });
      setNotifications(response.data.data || []);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreNotifications = async () => {
    if (loading || loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const offset = (nextPage - 1) * PAGE_SIZE;
      const response = await apiClient.get<NotificationsResponse>('/user-notifications/', {
        params: { limit: PAGE_SIZE, offset }
      });
      const newData = response.data.data || [];
      setNotifications(prev => [...prev, ...newData]);
      setTotalCount(response.data.count || 0);
      setPage(nextPage);
    } catch (error) {
      console.error('Failed to load more notifications:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Infinite scroll handler
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    // Load more when within 50px of bottom
    if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !loading && !loadingMore) {
      loadMoreNotifications();
    }
  }, [hasMore, loading, loadingMore]);

  const handleMarkAsRead = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await apiClient.put(`/user-notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiClient.put('/user-notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id, { stopPropagation: () => { } } as React.MouseEvent);
    }
    setIsOpen(false);
    if (notification.category === 'recognition') {
      // Deep-link to the recognition page ONLY if we have a specific ID to highlight
      const recognitionId = (notification as any).reference_id ?? (notification as any).recognition_id;
      if (recognitionId) {
        const params = new URLSearchParams({
          notification_id: String(notification.id),
          recognition_id: String(recognitionId)
        });
        navigate(`/my-space/recognitions?${params.toString()}`);
      } else {
        // Fallback to the generic notifications template page
        navigate(`/workforce/notifications?id=${notification.id}`);
      }
    } else {
      navigate(`/workforce/notifications?id=${notification.id}`);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-600" weight={isOpen ? 'fill' : 'regular'} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-slate-50">
            <div>
              <h3 className="font-semibold text-gray-900">Notifications</h3>
              <p className="text-xs text-gray-500">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
              </p>
            </div>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleMarkAllAsRead}>
                <CheckCircle className="w-4 h-4 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notifications List with Infinite Scroll */}
          <div
            ref={listRef}
            className="max-h-80 overflow-y-auto"
            onScroll={handleScroll}
          >
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <>
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0 transition-colors ${!notification.is_read ? 'bg-blue-50/50' : ''
                      }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${getPriorityColor(notification.priority)}`}>
                            {notification.priority}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <h4 className={`text-sm ${notification.is_read ? 'text-gray-600' : 'text-gray-900 font-medium'} line-clamp-1`}>
                          {notification.title}
                        </h4>
                        {notification.content && (
                          <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                            {notification.content}
                          </p>
                        )}
                      </div>
                      {!notification.is_read && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></span>
                      )}
                    </div>
                  </div>
                ))}

                {/* Loading more indicator */}
                {loadingMore && (
                  <div className="flex items-center justify-center py-3">
                    <div className="animate-spin w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full" />
                    <span className="ml-2 text-xs text-gray-500">Loading...</span>
                  </div>
                )}

                {/* End of list */}
                {!hasMore && notifications.length > 0 && (
                  <div className="py-2 text-center">
                    <span className="text-[10px] text-gray-400">No more notifications</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 bg-slate-50">
            <Button
              variant="ghost"
              className="w-full justify-center text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
              onClick={() => {
                setIsOpen(false);
                navigate('/workforce/notifications');
              }}
            >
              View All Notifications
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
